"use client";

import { Divider } from "../../../elements/divider/divider";
import { cn } from "../../../lib/utils";
import { Column } from "../../grid/column";
import { Container } from "../../grid/container";
import { Row } from "../../grid/row";
import type {
  MainNavigationColumn,
  MainNavigationLink,
  MainNavigationMegaMenu,
  MainNavigationTeaser,
} from "../main-navigation.types";
import { NavigationColumn } from "../navigation-column/navigation-column";
import { NavigationServiceLinks } from "../navigation-service-links/navigation-service-links";
import { NavigationTeaser } from "../navigation-teaser/navigation-teaser";

export interface MegaMenuProps {
  id: string;
  megaMenu: MainNavigationMegaMenu;
  hidden: boolean;
  onLinkClick?: () => void;
}

/** Grid columns the aside of the second row takes, by its content. */
const ASIDE_SLOTS = {
  empty: 0,
  oneColumn: 2,
  twoColumns: 4,
} as const;

type AsideSlots = (typeof ASIDE_SLOTS)[keyof typeof ASIDE_SLOTS];

const ASIDE_CLASSES: Record<AsideSlots, string> = {
  [ASIDE_SLOTS.empty]: "",
  [ASIDE_SLOTS.oneColumn]: "col-span-2",
  [ASIDE_SLOTS.twoColumns]: "col-span-4",
};

// The teaser block keeps its width and starts right after the aside, so teasers
// move left in mega menus with fewer secondary columns instead of floating in
// the middle of the row.
const TEASER_CLASSES: Record<AsideSlots, string> = {
  [ASIDE_SLOTS.empty]: "col-span-8 col-start-1",
  [ASIDE_SLOTS.oneColumn]: "col-span-8 col-start-3",
  [ASIDE_SLOTS.twoColumns]: "col-span-8 col-start-5",
};

/**
 * Secondary columns sit on the same grid as the first row: one takes a single
 * slot pair, more take two and wrap below each other. Service links are as wide
 * as the design's two columns even when a mega menu has none.
 */
function getAsideSlots(
  secondaryColumnCount: number,
  serviceLinkCount: number
): AsideSlots {
  if (serviceLinkCount > 0 || secondaryColumnCount > 1) {
    return ASIDE_SLOTS.twoColumns;
  }
  return secondaryColumnCount === 0 ? ASIDE_SLOTS.empty : ASIDE_SLOTS.oneColumn;
}

/**
 * Desktop dropdown of a level 1 item, shown below the header bar. It is
 * positioned against the bar, so no element in between may be positioned.
 */
export function MegaMenu(props: Readonly<MegaMenuProps>) {
  const secondaryColumns = props.megaMenu.secondaryColumns ?? [];
  const serviceLinks = props.megaMenu.serviceLinks ?? [];
  const teasers = props.megaMenu.teasers ?? [];
  const asideSlots = getAsideSlots(
    secondaryColumns.length,
    serviceLinks.length
  );

  return (
    // The transparent top padding keeps the pointer inside the menu while it
    // crosses the gap below the header bar.
    <div
      className={cn(
        "absolute inset-x-0 top-full z-10 pt-4",
        // Tailwind compiles the translate utilities to the `translate`
        // property, so `transform` alone would not transition them.
        // `transition-discrete` holds the panel rendered until the closing
        // transition has finished, so `hidden` still drives both the
        // accessibility tree and the animation.
        "transition-[opacity,translate,display] transition-discrete duration-200 ease-out",
        "starting:-translate-y-3 starting:opacity-0",
        props.hidden ? "-translate-y-3 opacity-0" : "translate-y-0 opacity-100",
        "motion-reduce:transition-none"
      )}
      data-main-navigation-panel=""
      data-nav-context="mega_menu"
      hidden={props.hidden}
      id={props.id}
    >
      <Container
        className="max-h-[max(0px,calc(100dvh-var(--site-header-offset,5rem)-1rem))] overflow-y-auto rounded-lg bg-white py-10"
        fullWidth={true}
        paddingBottom="none"
        paddingTop="none"
        testId="mega-menu"
      >
        <Row>
          {props.megaMenu.columns.map((column) => (
            <Column className="col-span-2" key={column.id}>
              <NavigationColumn
                column={column}
                onLinkClick={props.onLinkClick}
                variant="primary"
              />
            </Column>
          ))}

          <MegaMenuSecondRow
            asideSlots={asideSlots}
            onLinkClick={props.onLinkClick}
            secondaryColumns={secondaryColumns}
            serviceLinks={serviceLinks}
            teasers={teasers}
          />
        </Row>
      </Container>
    </div>
  );
}

/**
 * Secondary columns, service links and teasers below the divider. Nothing is
 * rendered when a mega menu has none of them.
 */
function MegaMenuSecondRow(
  props: Readonly<{
    asideSlots: AsideSlots;
    secondaryColumns: MainNavigationColumn[];
    serviceLinks: MainNavigationLink[];
    teasers: MainNavigationTeaser[];
    onLinkClick?: () => void;
  }>
) {
  const hasAside = props.asideSlots !== ASIDE_SLOTS.empty;
  if (!(hasAside || props.teasers.length > 0)) {
    return null;
  }

  return (
    <>
      <Column className="col-span-12">
        {/* Together with the row gap, the divider is 40px away from both rows. */}
        <Divider className="my-[calc(--spacing(10)-var(--grid-unit-xl))]" />
      </Column>
      {hasAside ? (
        <Column
          className={cn(
            ASIDE_CLASSES[props.asideSlots],
            "flex flex-col justify-between gap-12"
          )}
        >
          {props.secondaryColumns.length > 0 ? (
            <div
              className={cn(
                "grid gap-unit",
                props.secondaryColumns.length === 1
                  ? "grid-cols-1"
                  : "grid-cols-2"
              )}
            >
              {props.secondaryColumns.map((column) => (
                <NavigationColumn
                  column={column}
                  key={column.id}
                  onLinkClick={props.onLinkClick}
                  variant="secondary"
                />
              ))}
            </div>
          ) : null}
          {props.serviceLinks.length > 0 ? (
            <NavigationServiceLinks
              links={props.serviceLinks}
              onLinkClick={props.onLinkClick}
              size="xs"
            />
          ) : null}
        </Column>
      ) : null}
      {props.teasers.length > 0 ? (
        <Column className={TEASER_CLASSES[props.asideSlots]}>
          {/* A fourth teaser starts a second line at the same width. */}
          <ul className="grid grid-cols-3 gap-3">
            {props.teasers.map((teaser) => (
              <li key={teaser.id}>
                <NavigationTeaser
                  aspectRatio="1:1"
                  onLinkClick={props.onLinkClick}
                  sizes="(min-width: 1400px) 280px, 20vw"
                  teaser={teaser}
                  variant="default"
                />
              </li>
            ))}
          </ul>
        </Column>
      ) : null}
    </>
  );
}
